import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { Search, Eye, User, CheckCircle2, XCircle, Pause, Filter, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUSES = [
  { value: 'new', label: 'New', cls: 'badge-info' },
  { value: 'shortlisted', label: 'Shortlisted', cls: 'badge-warn' },
  { value: 'selected', label: 'Selected', cls: 'badge-success' },
  { value: 'rejected', label: 'Rejected', cls: 'badge-danger' },
  { value: 'hold', label: 'Hold', cls: 'badge-muted' },
];

export default function Candidates() {
  const [candidates, setCandidates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (q) params.q = q;
    const { data } = await api.get('/candidates', { params });
    setCandidates(data);
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="candidates-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Module 04 · HR</div>
          <h1 className="heading text-3xl font-black text-ink">Candidates</h1>
          <p className="text-sm text-zinc-500 mt-1">Review applications from the front-desk kiosk. Tag, shortlist & reuse.</p>
        </div>
        <div className="flex gap-2">
          <a href="/apply" target="_blank" rel="noreferrer" className="btn-outline" data-testid="kiosk-link">Open Kiosk Form →</a>
        </div>
      </header>

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input className="input pl-9" placeholder="Search name, phone, post…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="search-candidates" />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setFilter('all')} className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${filter === 'all' ? 'bg-ink text-white border-ink' : 'bg-white border-zinc-300'}`} data-testid="filter-all">All</button>
          {STATUSES.map((s) => (
            <button key={s.value} onClick={() => setFilter(s.value)}
              data-testid={`filter-${s.value}`}
              className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${filter === s.value ? 'bg-ink text-white border-ink' : 'bg-white border-zinc-300'}`}
            >{s.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {candidates.length === 0 && <div className="card p-12 text-center text-zinc-400 col-span-full">No applications yet.</div>}
        {candidates.map((c) => (
          <div key={c.id} className="card p-4 card-hover cursor-pointer" onClick={() => setSelected(c)} data-testid={`candidate-${c.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary text-white rounded-sm flex items-center justify-center heading font-black text-xl">
                  {(c.name || '?').slice(0,1).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-ink">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.phone}</div>
                </div>
              </div>
              <span className={`${STATUSES.find((s) => s.value === c.status)?.cls || 'badge-muted'}`}>{c.status}</span>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2"><Tag size={12} className="text-zinc-400" /> <span className="font-semibold">{c.applied_post}</span></div>
              <div className="text-xs text-zinc-500">Qualification: {c.qualification || '—'}</div>
              <div className="text-xs text-zinc-500">Experience: {c.experience_years != null ? `${c.experience_years} yrs` : '—'}</div>
              <div className="text-xs text-zinc-500">Expected: {c.expected_salary != null ? `₹${c.expected_salary}` : '—'}</div>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 text-[10px] text-zinc-400">
              Applied {c.created_at && format(new Date(c.created_at), 'dd MMM HH:mm')}
            </div>
          </div>
        ))}
      </div>

      {selected && <CandidateDetail c={selected} onClose={() => setSelected(null)} onUpdated={() => { load(); setSelected(null); }} />}
    </div>
  );
}

function CandidateDetail({ c, onClose, onUpdated }) {
  const [status, setStatus] = useState(c.status);
  const [notes, setNotes] = useState(c.hr_notes || '');
  const [saving, setSaving] = useState(false);

  const save = async (newStatus) => {
    setSaving(true);
    try {
      await api.patch(`/candidates/${c.id}`, { status: newStatus || status, hr_notes: notes });
      toast.success('Candidate updated');
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={c.name} subtitle={`Applied for: ${c.applied_post}`} size="lg"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Close</button>
        <button className="btn-secondary" onClick={() => save()} disabled={saving} data-testid="save-candidate">Save</button>
      </>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Section title="Basic Info">
            <Row label="Name" value={c.name} />
            <Row label="Phone" value={c.phone} />
            <Row label="DOB" value={c.dob} />
            <Row label="Marital" value={c.marital_status} />
            <Row label="Father/Husband" value={c.father_husband_name} />
            <Row label="Address" value={c.address} />
          </Section>
          <Section title="Experience">
            <Row label="Years" value={c.experience_years} />
            <Row label="Current" value={c.current_employer} />
            <Row label="Previous" value={c.previous_employer} />
            <Row label="Reference" value={c.reference_name && `${c.reference_name} · ${c.reference_phone}`} />
            <Row label="Reason Left" value={c.reason_for_leaving} />
          </Section>
        </div>
        <div>
          <Section title="Education & Skills">
            <Row label="Qualification" value={c.qualification} />
            <Row label="Computer" value={c.computer_knowledge ? 'Yes' : 'No'} />
            <Row label="Typing Hindi" value={c.typing_hindi ? 'Yes' : 'No'} />
            <Row label="Typing English" value={c.typing_english ? 'Yes' : 'No'} />
            <Row label="Software" value={(c.software_skills || []).join(', ')} />
          </Section>
          <Section title="Salary & Other">
            <Row label="Current ₹" value={c.current_salary != null ? `₹${c.current_salary}` : null} />
            <Row label="Expected ₹" value={c.expected_salary != null ? `₹${c.expected_salary}` : null} />
            <Row label="Referred by" value={c.referred_by} />
            <Row label="Strengths" value={c.strengths} />
            <Row label="Weaknesses" value={c.weaknesses} />
          </Section>
        </div>
      </div>

      <div className="border-t border-zinc-200 mt-6 pt-6">
        <div className="overline mb-2">HR Actions</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {STATUSES.map((s) => (
            <button key={s.value} onClick={() => { setStatus(s.value); save(s.value); }}
              data-testid={`set-status-${s.value}`}
              className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${status === s.value ? 'bg-primary text-white border-primary' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}
            >{s.label}</button>
          ))}
        </div>
        <label className="label">HR Notes</label>
        <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="hr-notes" />
      </div>
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="overline mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="overline w-28 flex-shrink-0 text-[10px]">{label}</span>
      <span className="font-medium text-ink break-words">{value || <span className="text-zinc-300">—</span>}</span>
    </div>
  );
}
